import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Pagination,
  Spinner,
  Table,
} from "flowbite-react";
import {
  HiOutlineOfficeBuilding,
  HiOutlineArrowRight,
  HiOutlineDownload,
} from "react-icons/hi";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import moment from "moment";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";
import AdvancedFilters from "../components/filters/AdvancedFilters";
import { FIELD_TYPES, YES_NO_OPTIONS } from "../components/filters/filterUtils";
import useAdvancedFilters from "../hooks/useAdvancedFilters";
import useAdminFilterOptions from "../hooks/useAdminFilterOptions";

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
};

const formatInterval = (months) => {
  const raw = Number(months);
  if (!Number.isFinite(raw) || raw <= 0) return "-";
  if (raw === 1) return "Mensal";
  if (raw === 3) return "Trimestral";
  if (raw === 12) return "Anual";
  return `${raw} meses`;
};

const formatExpiration = (date) => {
  if (!date) return "—";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
};

const STATUS_META = {
  "-1": { label: "Inativa", color: "gray" },
  0: { label: "Bloqueada", color: "failure" },
  1: { label: "Ativa", color: "success" },
};

const DEFAULTS = {
  scope: "parents",
  sortBy: "name",
  sortDir: "ASC",
  itemsPerPage: "20",
};

export default function CompaniesOverviewPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const { options, isLoading: isLoadingOptions } = useAdminFilterOptions();

  /* ---------------------------------------------------------------- *
   * Schema dos filtros — cada campo vira input, chip e query param.
   * ---------------------------------------------------------------- */
  const fields = useMemo(
    () => [
      { key: "search", type: FIELD_TYPES.SEARCH, label: "Busca" },

      // Barra do topo
      {
        key: "sortBy",
        label: "Ordenar por",
        type: FIELD_TYPES.SELECT,
        inline: true,
        options: [
          { value: "name", label: "Nome" },
          { value: "socialReason", label: "Razão social" },
          { value: "createdAt", label: "Cadastro" },
          { value: "updatedAt", label: "Atualização" },
          { value: "planExpirationDate", label: "Vencimento" },
          { value: "status", label: "Status" },
          { value: "credit", label: "Crédito" },
          { value: "incomeMonth", label: "Faturamento" },
          { value: "licenseLimit", label: "Licenças" },
          { value: "usersCount", label: "Nº de usuários" },
          { value: "funnelsCount", label: "Nº de funis" },
          { value: "teamsCount", label: "Nº de times" },
          { value: "filiaisCount", label: "Nº de filiais" },
        ],
      },
      {
        key: "sortDir",
        label: "Direção",
        type: FIELD_TYPES.SELECT,
        inline: true,
        options: [
          { value: "ASC", label: "Crescente" },
          { value: "DESC", label: "Decrescente" },
        ],
      },
      {
        key: "itemsPerPage",
        label: "Por página",
        type: FIELD_TYPES.SELECT,
        inline: true,
        options: [10, 20, 50, 100].map((value) => ({
          value: String(value),
          label: `${value} / pág.`,
        })),
      },

      // ---- Contrato ----
      {
        key: "planId",
        label: "Plano",
        type: FIELD_TYPES.MULTISELECT,
        group: "Contrato",
        isLoading: isLoadingOptions,
        options: [
          { value: "none", label: "Sem plano (teste)" },
          ...options.plans,
        ],
      },
      {
        key: "planCycleId",
        label: "Ciclo do plano",
        type: FIELD_TYPES.MULTISELECT,
        group: "Contrato",
        isLoading: isLoadingOptions,
        options: options.planCycles,
      },
      {
        key: "intervalMonths",
        label: "Periodicidade",
        type: FIELD_TYPES.MULTISELECT,
        group: "Contrato",
        options: options.intervalMonths,
      },
      {
        key: "trialStatus",
        label: "Situação comercial",
        type: FIELD_TYPES.SELECT,
        group: "Contrato",
        options: [
          { value: "", label: "Todas" },
          { value: "trial", label: "Em teste" },
          { value: "paid", label: "Contratado" },
        ],
      },
      {
        key: "typePayment",
        label: "Cobrança",
        type: FIELD_TYPES.MULTISELECT,
        group: "Contrato",
        options: [
          { value: "PRE", label: "Pré-pago" },
          { value: "POS", label: "Pós-pago" },
        ],
      },
      {
        key: "expirationStatus",
        label: "Vencimento",
        type: FIELD_TYPES.SELECT,
        group: "Contrato",
        options: [
          { value: "", label: "Todos" },
          { value: "expired", label: "Expirado" },
          { value: "expiring", label: "A vencer" },
          { value: "active", label: "Vigente" },
          { value: "none", label: "Sem data" },
        ],
      },
      {
        key: "expiringInDays",
        label: "A vencer em",
        type: FIELD_TYPES.SELECT,
        group: "Contrato",
        hint: "usa 'A vencer'",
        options: [
          { value: "", label: "30 dias (padrão)" },
          ...[7, 15, 30, 60, 90].map((days) => ({
            value: String(days),
            label: `${days} dias`,
          })),
        ],
      },

      {
        fromKey: "incomeMin",
        toKey: "incomeMax",
        label: "Faturamento mensal",
        type: FIELD_TYPES.NUMBER_RANGE,
        group: "Contrato",
        min: 0,
      },
      {
        fromKey: "licenseMin",
        toKey: "licenseMax",
        label: "Licenças contratadas",
        type: FIELD_TYPES.NUMBER_RANGE,
        group: "Contrato",
        min: 0,
      },

      // ---- Cadastro ----
      {
        key: "status",
        label: "Status",
        type: FIELD_TYPES.MULTISELECT,
        group: "Cadastro",
        options: [
          { value: "1", label: "Ativa" },
          { value: "0", label: "Bloqueada" },
          { value: "-1", label: "Inativa" },
        ],
      },
      {
        key: "scope",
        label: "Escopo",
        type: FIELD_TYPES.SELECT,
        group: "Cadastro",
        options: [
          { value: "parents", label: "Somente matrizes" },
          { value: "filiais", label: "Somente filiais" },
          { value: "all", label: "Matrizes e filiais" },
        ],
      },
      {
        key: "segmentId",
        label: "Segmento",
        type: FIELD_TYPES.MULTISELECT,
        group: "Cadastro",
        isLoading: isLoadingOptions,
        options: [
          { value: "none", label: "Sem segmento" },
          ...options.segments,
        ],
      },
      {
        fromKey: "createdStart",
        toKey: "createdEnd",
        label: "Cadastrada entre",
        type: FIELD_TYPES.DATE_RANGE,
        group: "Cadastro",
      },

      // ---- Localização ----
      {
        key: "city",
        label: "Cidade",
        type: FIELD_TYPES.SELECT,
        group: "Localização",
        options: [{ value: "", label: "Todas" }, ...options.cities],
      },
      {
        key: "neighborhood",
        label: "Bairro",
        type: FIELD_TYPES.TEXT,
        group: "Localização",
        placeholder: "Contém...",
      },
      {
        key: "cep",
        label: "CEP",
        type: FIELD_TYPES.TEXT,
        group: "Localização",
        placeholder: "Começa com...",
      },

      // ---- Onboarding ----
      {
        key: "onbCompleted",
        label: "Onboarding concluído",
        type: FIELD_TYPES.BOOLEAN,
        group: "Onboarding",
        options: YES_NO_OPTIONS,
      },
      {
        key: "onbSalesModel",
        label: "Modelo de venda",
        type: FIELD_TYPES.MULTISELECT,
        group: "Onboarding",
        options: options.salesModels,
      },
      {
        key: "onbSize",
        label: "Porte",
        type: FIELD_TYPES.MULTISELECT,
        group: "Onboarding",
        options: options.sizes,
      },
      {
        key: "onbAvgTicketBand",
        label: "Ticket médio",
        type: FIELD_TYPES.MULTISELECT,
        group: "Onboarding",
        options: options.ticketBands,
      },

      // ---- Integrações ----
      {
        key: "hasWhatsapp",
        label: "WhatsApp conectado",
        type: FIELD_TYPES.BOOLEAN,
        group: "Integrações",
        options: YES_NO_OPTIONS,
      },
      {
        key: "whatsappAiAgentEnabled",
        label: "Agente de IA ativo",
        type: FIELD_TYPES.BOOLEAN,
        group: "Integrações",
        options: YES_NO_OPTIONS,
      },
      {
        key: "aiProvider",
        label: "Provedor de IA",
        type: FIELD_TYPES.MULTISELECT,
        group: "Integrações",
        options: options.aiProviders,
      },
      {
        key: "hasAiKey",
        label: "Chave de IA própria",
        type: FIELD_TYPES.BOOLEAN,
        group: "Integrações",
        options: YES_NO_OPTIONS,
      },
      {
        key: "hasVoip",
        label: "Telefonia configurada",
        type: FIELD_TYPES.BOOLEAN,
        group: "Integrações",
        options: YES_NO_OPTIONS,
      },
      {
        key: "voipProvider",
        label: "Provedor de telefonia",
        type: FIELD_TYPES.MULTISELECT,
        group: "Integrações",
        options: options.voipProviders,
      },
      {
        key: "hasAsaas",
        label: "Cliente no Asaas",
        type: FIELD_TYPES.BOOLEAN,
        group: "Integrações",
        options: YES_NO_OPTIONS,
      },
      {
        key: "hasSubscription",
        label: "Assinatura recorrente",
        type: FIELD_TYPES.BOOLEAN,
        group: "Integrações",
        options: YES_NO_OPTIONS,
      },
      {
        key: "hasLogo",
        label: "Logo enviada",
        type: FIELD_TYPES.BOOLEAN,
        group: "Integrações",
        options: YES_NO_OPTIONS,
      },

      // ---- Uso da plataforma ----
      {
        fromKey: "minUsers",
        toKey: "maxUsers",
        label: "Usuários",
        type: FIELD_TYPES.NUMBER_RANGE,
        group: "Uso da plataforma",
        min: 0,
      },
      {
        fromKey: "minFunnels",
        toKey: "maxFunnels",
        label: "Funis",
        type: FIELD_TYPES.NUMBER_RANGE,
        group: "Uso da plataforma",
        min: 0,
      },
      {
        fromKey: "minTeams",
        toKey: "maxTeams",
        label: "Times",
        type: FIELD_TYPES.NUMBER_RANGE,
        group: "Uso da plataforma",
        min: 0,
      },
      {
        fromKey: "minFiliais",
        toKey: "maxFiliais",
        label: "Filiais",
        type: FIELD_TYPES.NUMBER_RANGE,
        group: "Uso da plataforma",
        min: 0,
      },
    ],
    [options, isLoadingOptions],
  );

  const { values, setValues, reset, params, queryKey } = useAdvancedFilters({
    fields,
    defaults: DEFAULTS,
  });

  // Qualquer mudança de filtro devolve a listagem para a primeira página.
  useEffect(() => {
    setCurrentPage(1);
  }, [queryKey]);

  const quickFilters = useMemo(
    () => [
      {
        id: "expiring",
        label: "Vencendo em 30 dias",
        isActive: (current) => current.expirationStatus === "expiring",
        apply: (current, active) => ({
          ...current,
          expirationStatus: active ? "" : "expiring",
          expiringInDays: active ? "" : "30",
        }),
      },
      {
        id: "expired",
        label: "Expiradas",
        isActive: (current) => current.expirationStatus === "expired",
        apply: (current, active) => ({
          ...current,
          expirationStatus: active ? "" : "expired",
        }),
      },
      {
        id: "trial",
        label: "Em teste",
        isActive: (current) => current.trialStatus === "trial",
        apply: (current, active) => ({
          ...current,
          trialStatus: active ? "" : "trial",
        }),
      },
      {
        id: "blocked",
        label: "Bloqueadas",
        isActive: (current) => (current.status || []).includes("0"),
        apply: (current, active) => ({
          ...current,
          status: active ? [] : ["0"],
        }),
      },
      {
        id: "no-onboarding",
        label: "Sem onboarding",
        isActive: (current) => current.onbCompleted === "false",
        apply: (current, active) => ({
          ...current,
          onbCompleted: active ? "" : "false",
        }),
      },
    ],
    [],
  );

  const {
    data: overview,
    isLoading,
    isFetching,
    error,
  } = useQuery(
    ["companies-overview", queryKey, currentPage],
    async () => {
      const response = await axios.get(ADMIN.GET_COMPANIES_OVERVIEW, {
        params: { ...params, page: currentPage },
      });
      return response.data?.data || null;
    },
    { keepPreviousData: true, staleTime: 60 * 1000 },
  );

  const totals = overview?.totals ?? {
    companies: 0,
    funnels: 0,
    teams: 0,
    users: 0,
    filiais: 0,
  };
  const companies = overview?.companies ?? [];
  const pagination = overview?.pagination ?? {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  };

  /** Exporta exatamente o recorte filtrado (sem paginação) em CSV. */
  const exportCsv = async () => {
    const response = await axios.get(ADMIN.GET_COMPANIES_OVERVIEW, {
      params: { ...params, itemsPerPage: -1 },
    });
    const rows = response.data?.data?.companies || [];
    const header = [
      "id",
      "nome",
      "razao_social",
      "documento",
      "email",
      "telefone",
      "plano",
      "ciclo",
      "status",
      "vencimento",
      "usuarios",
      "funis",
      "times",
      "filiais",
      "cidade",
      "cadastro",
    ];
    const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [
      header.join(";"),
      ...rows.map((company) =>
        [
          company.id,
          company.name,
          company.socialReason,
          company.document,
          company.email,
          company.phoneNumber,
          company.plan?.name || "Teste",
          formatInterval(company.planCycle?.intervalMonths),
          STATUS_META[String(company.status)]?.label || company.status,
          company.planExpirationDate
            ? moment(company.planExpirationDate).format("DD/MM/YYYY")
            : "",
          company.counts?.users ?? 0,
          company.counts?.funnels ?? 0,
          company.counts?.teams ?? 0,
          company.counts?.filiais ?? 0,
          company.addressCity,
          moment(company.createdAt).format("DD/MM/YYYY"),
        ]
          .map(escape)
          .join(";"),
      ),
    ].join("\n");

    const blob = new Blob([`﻿${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `empresas-${moment().format("YYYY-MM-DD-HHmm")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-100 text-orange-800">
            <HiOutlineOfficeBuilding className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
              Empresas Relinq
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">Clientes</h1>
          </div>
        </div>
      </header>

      {/* Totais do recorte filtrado */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["Empresas", totals.companies],
          ["Usuários", totals.users],
          ["Funis", totals.funnels],
          ["Filiais", totals.filiais],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
              {label}
            </p>
            <p className="text-xl font-semibold text-slate-900">{value ?? 0}</p>
          </div>
        ))}
      </div>

      <AdvancedFilters
        subtitle="Refine a lista de empresas"
        fields={fields}
        values={values}
        onChange={setValues}
        onReset={reset}
        quickFilters={quickFilters}
        searchPlaceholder="Nome, razão social, documento, e-mail, telefone, responsável ou ID"
        resultsLabel={
          overview
            ? `Mostrando ${companies.length} de ${pagination.totalCount} empresas${
                isFetching ? " · atualizando..." : ""
              }`
            : null
        }
        extraActions={
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            <HiOutlineDownload className="h-3.5 w-3.5" />
            Exportar CSV
          </button>
        }
      />

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Spinner color="info" />
            Carregando informações das empresas...
          </div>
        ) : error ? (
          <Alert color="failure">
            <span className="font-semibold">
              Não foi possível carregar os dados.
            </span>{" "}
            Tente novamente mais tarde.
          </Alert>
        ) : companies.length === 0 ? (
          <div className="p-6 text-center text-sm text-orange-600">
            Nenhuma empresa encontrada com os filtros selecionados.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[1180px]">
                <Table.Head className="bg-slate-50 text-slate-500">
                  {[
                    "Empresa",
                    "Plano",
                    "Situação",
                    "Expiração",
                    "Métricas",
                    "Cadastro",
                    "",
                  ].map((label, index) => (
                    <Table.HeadCell
                      key={label || index}
                      className="text-[11px] uppercase tracking-[0.3em]"
                    >
                      {label}
                    </Table.HeadCell>
                  ))}
                </Table.Head>
                <Table.Body className="divide-y divide-slate-100">
                  {companies.map((company) => {
                    const label =
                      company.socialReason ||
                      company.name ||
                      `Empresa ${company.id}`;
                    const planName =
                      company.plan?.name || company.plan?.code || "Sem plano";
                    const cycleLabel =
                      company.planCycle?.description ||
                      formatInterval(company.planCycle?.intervalMonths);
                    const statusMeta = STATUS_META[String(company.status)] || {
                      label: company.status,
                      color: "gray",
                    };

                    return (
                      <Table.Row
                        key={company.id}
                        className="bg-white hover:bg-slate-50"
                      >
                        <Table.Cell className="py-2 pr-3">
                          <p className="text-sm font-semibold text-slate-900">
                            {label}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {company.document || company.email || "Sem contato"}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {company.Segment?.name && (
                              <Badge color="gray" size="xs">
                                {company.Segment.name}
                              </Badge>
                            )}
                            {company.addressCity && (
                              <Badge color="gray" size="xs">
                                {company.addressCity}
                              </Badge>
                            )}
                            {company.hasWhatsapp && (
                              <Badge color="green" size="xs">
                                WhatsApp
                              </Badge>
                            )}
                            {company.whatsappAiAgentEnabled && (
                              <Badge color="purple" size="xs">
                                IA
                              </Badge>
                            )}
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <p className="text-sm font-semibold text-slate-900">
                            {planName}
                          </p>
                          <p className="-mt-1 text-xs text-gray-500">
                            {cycleLabel === "-"
                              ? formatCurrency(company.planCycle?.price)
                              : `${cycleLabel} · ${formatCurrency(
                                  company.planCycle?.price,
                                )}`}
                          </p>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <div className="flex flex-col items-start gap-1">
                            <Badge color={statusMeta.color} className="w-fit">
                              {statusMeta.label}
                            </Badge>
                            <Badge
                              color={company.isTrial ? "warning" : "success"}
                              className="w-fit"
                            >
                              {company.isTrial ? "Teste" : "Contratado"}
                            </Badge>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <span
                            className={`text-sm font-semibold ${
                              company.isExpired
                                ? "text-rose-600"
                                : "text-slate-900"
                            }`}
                          >
                            {formatExpiration(company.planExpirationDate)}
                          </span>
                          <p
                            className={`text-[11px] ${
                              company.isExpired
                                ? "text-rose-600"
                                : "text-gray-500"
                            }`}
                          >
                            {company.isExpired
                              ? "Plano expirado"
                              : "Próxima renovação"}
                          </p>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <div className="grid grid-cols-4 gap-1.5 text-center">
                            {[
                              ["Usu.", company.counts?.users],
                              ["Funis", company.counts?.funnels],
                              ["Times", company.counts?.teams],
                              ["Filiais", company.counts?.filiais],
                            ].map(([metric, value]) => (
                              <div
                                key={metric}
                                className="rounded-lg border border-slate-200 bg-slate-50 px-1 py-0.5"
                              >
                                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                  {metric}
                                </p>
                                <p className="text-sm font-semibold text-slate-900">
                                  {value ?? 0}
                                </p>
                              </div>
                            ))}
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-2 text-xs text-slate-500">
                          {moment(company.createdAt).format("DD/MM/YYYY")}
                          <p className="text-[11px] text-slate-400">
                            {company.onbCompleted
                              ? "Onboarding ok"
                              : "Onboarding pendente"}
                          </p>
                        </Table.Cell>
                        <Table.Cell className="py-2 text-center">
                          <Link to={`/companies/${company.id}`}>
                            <Button color="gray" size="xs" pill>
                              <HiOutlineArrowRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
            <div className="flex flex-col gap-3 bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-500">
                Página {pagination.currentPage} de {pagination.totalPages} ·{" "}
                {pagination.totalCount} empresas filtradas
              </p>
              {pagination.totalPages > 1 && (
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={setCurrentPage}
                  showIcons
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
